import React, { FC, MutableRefObject, ReactElement, useEffect, useRef, useState } from 'react'
import { ReactState } from '../../Common/Contexts'
import { StateSetter } from '../../Common/Types/Types'
import { IsolatorLocations, TourEntry, TourOverlayPosition } from '../Types/Types'

type Props = {
    tourEntry: TourEntry
    boundingRect: DOMRect
    bodyRect: DOMRect
    setTourOverlayPosition: StateSetter<Nullable<[TourOverlayPosition, DOMRect]>>
    spacingOffset?: number
    preferredPosition?: TourOverlayPosition
}

const BACKGROUND_COLOUR: string = 'rgba(0, 0, 0, 0.75)'

const calculateNormalised = (rects: DOMRect, spacingOffset: number): DOMRect => {
    const offset: number = spacingOffset ?? 0
    const halfOffset: number = offset / 2
    const xNormalised: number = rects.x - halfOffset
    const yNormalised: number = rects.y - halfOffset
    const widthNormalised: number = rects.width + offset
    const heightNormalised: number = rects.height + offset

    return new DOMRect(xNormalised, yNormalised, widthNormalised, heightNormalised)
}

export const CanvasOverlay: FC<Props> = ({
    tourEntry,
    boundingRect,
    bodyRect: { width: bodyWidth, height: bodyHeight },
    spacingOffset,
    setTourOverlayPosition,
    preferredPosition
}: Props): ReactElement => {
    const canvasRef: MutableRefObject<OnlyNullable<HTMLCanvasElement>> = useRef<HTMLCanvasElement>(null)
    const [canvasContext, setCanvasContext]: ReactState<Nullable<CanvasRenderingContext2D>> =
        useState<Nullable<CanvasRenderingContext2D>>(null)
    const { x, y, width: elementWidth, height: elementHeight }: DOMRect = boundingRect
    const isolatorLocations: IsolatorLocations = {
        left: new DOMRect(0, 0, x, bodyHeight),
        top: new DOMRect(0, 0, bodyWidth, y),
        right: new DOMRect(x + elementWidth, 0, bodyWidth - (x + elementWidth), bodyHeight),
        bottom: new DOMRect(0, y + elementHeight, bodyWidth, bodyHeight - (y + elementHeight))
    }

    const drawHole = (currentRect: DOMRect): void => {
        const {
            x: holeRectX,
            y: holeRectY,
            width: holeRectWidth,
            height: holeRectHeight
        }: DOMRect = calculateNormalised(currentRect, spacingOffset ?? 0)

        if (canvasContext) {
            canvasContext.fillStyle = 'black'
            canvasContext.fillRect(holeRectX, holeRectY, holeRectWidth, holeRectHeight)
        }
    }

    const buildCanvas = (): void => {
        if (canvasContext) {
            canvasContext.globalCompositeOperation = 'source-over'
            canvasContext.fillStyle = BACKGROUND_COLOUR
            canvasContext.fillRect(0, 0, canvasRef?.current?.width ?? 0, canvasRef?.current?.height ?? 0)
            canvasContext.globalCompositeOperation = 'destination-out'
        }
    }

    const clearCanvas = (): void => {
        if (canvasContext) {
            canvasContext.clearRect(0, 0, canvasRef?.current?.width ?? 0, canvasRef?.current?.height ?? 0)
        }
    }

    const constructCanvas = (): void => {
        if (boundingRect && canvasContext) {
            clearCanvas()
            buildCanvas()
            drawHole(boundingRect)
        }
    }

    useEffect((): void => {
        const canvasCtx: Nullable<CanvasRenderingContext2D> = canvasRef.current?.getContext('2d')

        if (canvasCtx) {
            setCanvasContext(canvasCtx)
        }
    }, [canvasRef.current])

    useEffect((): void => constructCanvas(), [boundingRect, canvasContext])

    useEffect((): void => {
        const largest: [TourOverlayPosition, DOMRect] = Object.entries(isolatorLocations).reduce(
            (
                [largestKey, largestRect]: [string, DOMRect],
                [nextKey, nextRect]: [string, DOMRect]
            ): [TourOverlayPosition, DOMRect] => {
                if (nextRect.width * nextRect.height > largestRect.width * largestRect.height) {
                    return [nextKey as TourOverlayPosition, nextRect]
                }

                return [largestKey as TourOverlayPosition, largestRect]
            }
        ) as [TourOverlayPosition, DOMRect]

        setTourOverlayPosition((): Nullable<[TourOverlayPosition, DOMRect]> => {
            if (
                preferredPosition &&
                isolatorLocations[preferredPosition].width > (tourEntry.minContentSize?.width ?? 300) &&
                isolatorLocations[preferredPosition].height > (tourEntry.minContentSize?.height ?? 150)
            ) {
                return [preferredPosition, isolatorLocations[preferredPosition]]
            }

            return largest
        })
    }, [JSON.stringify(isolatorLocations)])

    return boundingRect ? (
        <>
            <canvas className='tour-canvas-overlay' ref={canvasRef} width={bodyWidth} height={bodyHeight} />
            <div
                className='isolator'
                style={{
                    width: `${isolatorLocations.left.width}px`,
                    height: `${isolatorLocations.left.height}px`,
                    top: 0,
                    left: 0
                }}
            ></div>
            <div
                className='isolator'
                style={{
                    width: `${isolatorLocations.top.width}px`,
                    height: `${isolatorLocations.top.height}px`,
                    top: 0,
                    left: 0
                }}
            ></div>
            <div
                className='isolator'
                style={{
                    width: `${isolatorLocations.right.width}px`,
                    height: `${isolatorLocations.right.height}px`,
                    top: 0,
                    left: `${isolatorLocations.right.left}px`
                }}
            ></div>
            <div
                className='isolator'
                style={{
                    width: `${isolatorLocations.bottom.width}px`,
                    height: `${isolatorLocations.bottom.height}px`,
                    top: `${isolatorLocations.bottom.top}px`,
                    left: 0
                }}
            ></div>
        </>
    ) : (
        <></>
    )
}
